// native
import { Component, OnInit, ChangeDetectionStrategy, ViewChild, ElementRef, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { from, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

// service
import { GlobalsService } from '../core/services/globals.service';
import { UtilityService } from '../core/services/utility.service';
import { StylesService } from '../core/services/styles.service';
import { ToastService } from '../core/services/toast.service';

// models
import { IStyle, IStyleSearchResult, StylesInfo, ReformatPayload } from '../models';

// constants
import { DEFAULT_STYLE_SIZE, INITIAL_STYLE_PAGE, ONLINE_UNSUPPORTED_ERROR } from '../constants/constants';

@Component({
  selector: 'scs-bibliography',
  templateUrl: './bibliography.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'content'
  }
})
export class BibliographyComponent implements OnInit, OnDestroy {

  @ViewChild('searchInput') searchInputElement: ElementRef;
  @ViewChild('styleDrawer') styleDrawerElement: ElementRef;
  @ViewChild('styleList') styleListElement: ElementRef;
  @ViewChild('styleFavoriteList') styleFavoriteListElement: ElementRef;

  stylesInfoSubscription: Subscription;

  mergedStyles: IStyle[] = [];

  favoriteStyles: IStyle[] = [];
  favoriteCustomStyles: IStyle[] = [];

  displayedCustomStyles: IStyle[] = [];
  allCustomStyles: IStyle[] = [];

  selectedStyle: IStyle;
  isStyleSelectLoading: boolean = false;

  currentStylePage: number = INITIAL_STYLE_PAGE;
  term: string = '';

  isBibliographyUpdating: boolean = false;

  constructor(
    public globalsService: GlobalsService,
    private changeDetectorRef: ChangeDetectorRef,
    private utilityService: UtilityService,
    public stylesService: StylesService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.stylesInfoSubscription = this.stylesService.stylesInfo$
      .subscribe((response: StylesInfo) => {
        if (!response) {
          return;
        }

        this.getMergedStyles();

        this.favoriteStyles = this.stylesService.getClientFavoriteStyles();
        this.favoriteCustomStyles = this.stylesService.getClientFavoriteCustomStyles();
        this.selectedStyle = this.stylesService.getSelectedStyle();

        if (Array.isArray(response.custom_styles)) {
          this.allCustomStyles = [...response.custom_styles];
          this.utilityService.sortAlphabetically(this.allCustomStyles, 'title')
          this.displayedCustomStyles = [...this.allCustomStyles];
        }
      });

    this.calculateStyleListOffset();
  }

  ngOnDestroy() {
    this.stylesInfoSubscription?.unsubscribe();
  }

  private getMergedStyles() {
    const params = {
      term: this.term,
      page: this.currentStylePage,
      size: DEFAULT_STYLE_SIZE
    };

    this.stylesService.styleFilter && this.stylesService.styleFilter.filter(params).then((styles: IStyleSearchResult) => {
      this.mergedStyles = styles.results;
      this.changeDetectorRef.markForCheck();
    });
  }

  onSearch() {
    this.stylesService.isFavoritesView = false;
    this.utilityService.scrollToTop();
    this.resetSearchParams();

    this.term = (<HTMLInputElement>this.searchInputElement.nativeElement).value;

    this.displayedCustomStyles = this.allCustomStyles.filter(style => {
      return style?.title?.toLowerCase().includes(this.term?.toLowerCase());
    });

    this.getMergedStyles();
  }

  onClearSearch() {
    (<HTMLInputElement>this.searchInputElement.nativeElement).value = '';
    this.utilityService.scrollToTop();
    this.resetSearchParams();

    this.displayedCustomStyles = [...this.allCustomStyles];

    this.getMergedStyles();
  }

  private resetSearchParams() {
    this.term = '';
    this.currentStylePage = INITIAL_STYLE_PAGE;
  }

  onScrollDown() {
    this.currentStylePage++;

    const params = {
      term: this.term,
      page: this.currentStylePage,
      size: DEFAULT_STYLE_SIZE
    };

    return from(this.stylesService.styleFilter.filter(params))
      .pipe(
        map((stylesBatch: IStyleSearchResult) => {
          this.mergedStyles = this.mergedStyles.concat(stylesBatch.results);
          this.changeDetectorRef.markForCheck();
        })
      );
  }

  onSelectStyle(style: IStyle) {
    this.isStyleSelectLoading = true;

    this.stylesService.setDefaultStyle(style.id).subscribe((response: StylesInfo) => {
      this.selectedStyle = style;
      this.stylesService.setSelectedStyle(style);
      this.isStyleSelectLoading = false;

      this.calculateStyleListOffset();

      this.changeDetectorRef.markForCheck();
    }, (err => {
      this.isStyleSelectLoading = false;
    }));
  }

  onFavoritesToggle() {
    this.onClearSearch();

    this.stylesService.isFavoritesView = !this.stylesService.isFavoritesView;

    this.calculateStyleListOffset();
  }

  onStyleFavoriteClick(style: IStyle, event: any, isCustom: boolean = false) {
    event.stopPropagation();

    style.isFavoriteStatusLoading = true;

    if (!style.isFavorite) {
      this.stylesService.addFavoriteStyle(style.id).subscribe((response: StylesInfo) => {
        isCustom ? this.favoriteCustomStyles.push(style) : this.favoriteStyles.push(style);

        this.utilityService.sortAlphabetically(this.favoriteStyles, 'title');
        this.utilityService.sortAlphabetically(this.favoriteCustomStyles, 'name');

        isCustom ? this.stylesService.setClientCustomFavoriteStyles(this.favoriteCustomStyles) : this.stylesService.setClientFavoriteStyles(this.favoriteStyles);

        style.isFavorite = !style.isFavorite;
        style.isFavoriteStatusLoading = false;
        this.changeDetectorRef.markForCheck();
      }, error => {
        style.isFavoriteStatusLoading = false;
        this.changeDetectorRef.markForCheck();
      });
      return;
    }

    this.stylesService.removeFavoriteStyle(style.id).subscribe((response: StylesInfo) => {
      const filteredStyles = isCustom
        ? this.favoriteCustomStyles.filter(styleItem => styleItem.id !== style.id)
        : this.favoriteStyles.filter(styleItem => styleItem.id !== style.id);

      this.utilityService.sortAlphabetically(filteredStyles, 'title');
      this.utilityService.sortAlphabetically(this.favoriteCustomStyles, 'name');

      isCustom ? (this.favoriteCustomStyles = filteredStyles) : (this.favoriteStyles = filteredStyles);

      isCustom ? this.stylesService.setClientCustomFavoriteStyles(this.favoriteCustomStyles) : this.stylesService.setClientFavoriteStyles(this.favoriteStyles);

      style.isFavorite = !style.isFavorite;
      style.isFavoriteStatusLoading = false;
      this.changeDetectorRef.markForCheck();
    }, error => {
      style.isFavoriteStatusLoading = false;
      this.changeDetectorRef.markForCheck();
    });
  }

  async onUpdateBibliography() {
    this.isBibliographyUpdating = true;

    try {
      let style = await this.stylesService.getDocumentStyleFormatFromSelectedStyle();

      this.globalsService.appConfig.updateBibliography(style, this.globalsService.locale, this.globalsService.language, this.globalsService.isHyperlinksModeOn, this.globalsService.isSectionsModeOn)
        .then((response: ReformatPayload | void) => {
          this.isBibliographyUpdating = false;
          this.changeDetectorRef.markForCheck();

          if ((<ReformatPayload>response)?.canceledByUser) {
            return;
          }

          if ((<ReformatPayload>response)?.numCslErrCits) {
            this.toastService.showCslError((<ReformatPayload>response).numCslErrCits);
            return;
          }

          this.toastService.openToast({
            text: 'Bibliography is successfully updated.',
            type: 'success'
          });
        })
        .catch(err => {
          this.isBibliographyUpdating = false;
          this.changeDetectorRef.markForCheck();

          if (err?.message?.includes && err.message.includes(ONLINE_UNSUPPORTED_ERROR))
            return this.toastService.showWordOnlineError();

          this.toastService.openToast({
            text: 'There was an error while updating bibliography.',
            type: 'error'
          });
        });
    } catch {
      this.isBibliographyUpdating = false;
      this.changeDetectorRef.markForCheck();
      this.toastService.openToast({
        text: 'Something has gone wrong while updating the bibliography with selected style. Please try again.',
        type: 'error'
      });
    }
  }

  private calculateStyleListOffset() {
    setTimeout(() => {
      if (!this.styleDrawerElement?.nativeElement)
        return;

      const height = (<HTMLElement>this.styleDrawerElement.nativeElement).clientHeight;

      if (<HTMLElement>this.styleListElement?.nativeElement)
        (<HTMLElement>this.styleListElement.nativeElement).style.height = `calc(100% - ${height}px)`;

      if (<HTMLElement>this.styleFavoriteListElement?.nativeElement)
        (<HTMLElement>this.styleFavoriteListElement.nativeElement).style.height = `calc(100% - ${height}px)`;
    }, 200);
  }
}